import { faDownload } from "@fortawesome/pro-light-svg-icons";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import { Button } from "@vapor/react-material";
import Box from "@vapor/react-material/Box";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDownloadTaxSubmissionF24Pdf } from "../../../../../core/api/use-download-tax-submission-f24-pdf/useDownloadTaxSubmissionF24Pdf";
import { downloadFile } from "../../../../../core/utils/downloadFile";
import { PdfPreviewer } from "../../../../components/pdf-previewer/PdfPreviewer";

interface TaxSubmissionF24PreviewerProps {
    taxSubmissionId: string;
    taxPayerId: string;
    taxPayerPosition: number;
    delegationPosition?: number;
}

export const TaxSubmissionF24Previewer: React.FC<
    TaxSubmissionF24PreviewerProps
> = ({ taxSubmissionId, taxPayerId, taxPayerPosition, delegationPosition }) => {
    const { t } = useTranslation();
    const [downloading, setDownloading] = useState(false);
    const [file, setFile] = useState<{ content: Uint8Array; type: string }>();
    const [hasError, setHasError] = useState(false);

    const { mutateAsync: downloadF24PdfUseCase } =
        useDownloadTaxSubmissionF24Pdf();

    useEffect(() => {
        let cancelled = false;

        const download = async () => {
            try {
                const data = await downloadF24PdfUseCase({
                    taxSubmissionId,
                    taxPayerId,
                    taxPayerPosition,
                    delegationPosition,
                });
                if (cancelled) return;
                setFile({ content: data.content, type: data.type });
            } catch {
                if (cancelled) return;
                // error notification already shown by handleHttpError;
                // flip state to render a fallback instead of an infinite spinner.
                setHasError(true);
            }
        };

        download();

        return () => {
            cancelled = true;
        };
    }, [
        delegationPosition,
        downloadF24PdfUseCase,
        taxPayerId,
        taxPayerPosition,
        taxSubmissionId,
    ]);

    const handleDownload = async () => {
        if (!file) return;

        setDownloading(true);

        try {
            await downloadFile(file.content, `F24_${taxPayerId}`, file.type);
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box>
            {hasError ? (
                <Box marginTop="24px">
                    <Typography>
                        {t("components.outcome_previewer.error")}
                    </Typography>
                </Box>
            ) : !file ? (
                <LoadingWrapper
                    loading
                    spinnerProps={{
                        loadingText: t(
                            "components.loading_wrapper.loading_message",
                        ),
                    }}
                />
            ) : file.type === "application/pdf" ? (
                <PdfPreviewer file={file.content} />
            ) : (
                <Box marginTop="24px">
                    <Typography>
                        {t("components.outcome_previewer.no_preview")}
                    </Typography>
                </Box>
            )}
            <Box sx={{ ml: -4 }}>
                <VaporToolbar
                    size="large"
                    variant="regular"
                    contentRight={[
                        file && (
                            <Button
                                key="download"
                                size="large"
                                variant="contained"
                                disabled={downloading}
                                onClick={handleDownload}
                            >
                                <VaporIcon icon={faDownload} color="inverse" />
                                <Typography sx={{ marginLeft: 1 }}>
                                    {t(
                                        "views.home.components.tax_submission_download_sogei_error.download_sogei_error",
                                    )}
                                </Typography>
                            </Button>
                        ),
                    ]}
                />
            </Box>
        </Box>
    );
};
